import { useState } from "react";
import { EngineDecommissionPanel } from "./EngineDecommissionPanel";
import { useEngineDecommission, useEngineRecommission } from "@/hooks/migrations/engine";
import {
  engineErrorMessage,
  engineTaskPath,
  isDecommissionAbsent,
} from "@/services/migrations/engine";
import type { EngineTaskReport } from "@/services/migrations/engine";

/**
 * The stateful half of the decommission phase: the two mutations, the two typed tokens,
 * and the one runtime fact the report cannot carry.
 *
 * THE ROUTE MAY NOT BE THERE. `PLAYGROUND_MIGRATION_DECOMMISSION` is a third flag nested
 * inside the apply flag and default OFF, and with it off the routes are not mounted — not
 * in the route table, not in the OpenAPI document. `decommission.enabled` in the report
 * says so one round trip earlier and the panel blocks on it; this handles the case that
 * signal cannot cover, which is the flag flipping between the read and the click. A
 * 404/405/501 is read the way `isEngineAbsent` reads one — "the capability is not here" —
 * and NOT as a failure, because a route that was never mounted did nothing to the org.
 *
 * Everything visual lives in `EngineDecommissionPanel`, which is pure so its tests can
 * render it. This file holds only what a hook has to hold.
 */
export function EngineDecommissionSection({
  planId,
  task,
  family,
  orgId,
}: {
  planId: string;
  task: EngineTaskReport;
  family: string;
  orgId?: string | null;
}) {
  const taskPath = engineTaskPath(task);
  const [typedDecommission, setTypedDecommission] = useState("");
  const [typedRecommission, setTypedRecommission] = useState("");
  const decommission = useEngineDecommission(planId, taskPath);
  const recommission = useEngineRecommission(planId, taskPath);

  // The LAST result of either direction. They move the same task through the same phase,
  // so showing both at once would be showing two answers to one question.
  const result = recommission.data ?? decommission.data ?? null;
  const error = recommission.error ?? decommission.error ?? null;
  const routeAbsent = isDecommissionAbsent(error);

  return (
    <EngineDecommissionPanel
      task={task}
      family={family}
      orgId={orgId}
      typedDecommission={typedDecommission}
      typedRecommission={typedRecommission}
      onTypedDecommission={setTypedDecommission}
      onTypedRecommission={setTypedRecommission}
      onDecommission={() => decommission.mutate(typedDecommission)}
      onRecommission={() => recommission.mutate(typedRecommission)}
      pending={decommission.isPending || recommission.isPending}
      routeAbsent={routeAbsent}
      result={result}
      // A missing route is explained by the panel, not reported as a request failure.
      requestError={error && !routeAbsent ? engineErrorMessage(error) : null}
    />
  );
}
